<template>
  <div
    :class="[
      _.page,
      ready && _.ready,
      hdFade && _.hdFade,
      error && _.error,
      bgWhite && _.bgWhite,
    ]"
  >
    <div
      v-show="!isHideHeader"
      :class="[
        _.hd,
        ($slots.header || $scopedSlots.header) && _.custom,
        '__page_header__',
      ]"
    >
      <slot
        name="header"
        :handleBackClick="handleBackClick"
        :handleShareClick="handleShareClick"
        :pageHeaderHeight="pageHeaderHeight"
      >
        <x-icon name="back" :class="_.back" @click="handleBackClick" />
        <div :class="[_.title, _._ellipsis]">
          <slot name="title">
            {{ title }}
          </slot>
        </div>
        <div :class="_.extra">
          <slot name="extra">
            <x-icon
              v-if="!error && share.title"
              v-track="{
                name: '分享',
                id: 'cl7ig92nx000szqqe73zm7qsu',
              }"
              name="share"
              :class="_.share"
              @click="handleShareClick"
            />
          </slot>
        </div>
      </slot>
    </div>
    <div :class="_.bd">
      <div v-if="!ready || loading" :class="_.loading">
        <van-loading />
      </div>
      <div v-else-if="error" :class="_.error">
        <x-empty type="搜索" :message="error" no-margin />
      </div>
      <slot v-else :pageHeaderHeight="pageHeaderHeight" />
    </div>
  </div>
</template>

<script>
  import loveDcb from '@/utils/love.js'
  import Vue from 'vue'
  import { bindEvent, clamp } from 'vtils'
  import { dcb } from '@dcb/jssdk'
  import { globalModel } from '@/model'
  import { navigateBack } from '@/router'
  import { pageBridge } from '../page_bridge'
  import { pxToRem, setRootCssVar } from '@/utils'
  import { wechat } from '@/wechat'

  export default Vue.extend({
    props: {
      // 标题
      title: {
        type: String,
        default: '',
      },
      // 加载中
      loading: {
        type: Boolean,
        default: false,
      },
      // 分享参数
      share: {
        type: Object,
        default: () => ({}),
      },
      // 错误信息
      error: {
        type: String,
        default: '',
      },
      // 头部渐变
      hdFade: {
        type: Boolean,
        default: false,
      },
      // 白背景色
      bgWhite: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      ready: false,
      pageHeaderHeight: 0,
    }),

    computed: {
      isHideHeader() {
        return !globalModel.state.inDcb && !globalModel.state.inLove
      },
    },

    watch: {
      title: {
        immediate: true,
        handler() {
          this.updateTitle()
        },
      },
      share: {
        immediate: true,
        handler() {
          this.updateWechatShareData()
        },
      },
    },

    async mounted() {
      const env = await dcb.getEnv()
      let statusBarHeight = `${pxToRem(36)}rem`
      if (!env.dcb && !globalModel.state.inLove) {
        statusBarHeight = '0px'
      } else {
        if (env.ios) {
          // 苹果端：iPhone X 之后强制设为 36，之前设为 20
          statusBarHeight = `${pxToRem(
            window.screen.height > 800 ? 36 : 20,
          )}rem`
        } else {
          let getBarHeight
          if (globalModel.state.inLove) {
            getBarHeight = loveDcb.getStatusBarHeight
          } else {
            getBarHeight = dcb.getStatusBarHeight
          }
          // 安卓端：通过 jssdk 获取
          const { height } = await getBarHeight().catch(() => ({}))
          if (height != null) {
            statusBarHeight = `${
              height > 50 ? height / (window.devicePixelRatio || 2) : height
            }px`
          }
        }
      }
      setRootCssVar('status-bar-height', statusBarHeight)
      setRootCssVar('bold-value', env.ios ? 'bold' : '500')

      this.$nextTick(() => {
        const pageHeaderHeight = document
          .querySelector('.__page_header__')
          .getBoundingClientRect().height
        this.pageHeaderHeight = pageHeaderHeight
        setRootCssVar('page-header-height', `${pageHeaderHeight}px`)
        setRootCssVar('page-header-ratio', '0')
        setRootCssVar('page-header-action-color', 'white')
        setRootCssVar('page-header-title-color', 'rgba(0, 0, 0, 0)')
        this.$once(
          'hook:beforeDestroy',
          bindEvent(window)('scroll', () => {
            const ratio = clamp(window.scrollY / pageHeaderHeight, 0, 1)
            pageBridge.emit('scroll', {
              scrollTop: window.scrollY,
              headerParallaxRate: ratio,
            })
            setRootCssVar('page-header-ratio', ratio)
            setRootCssVar(
              'page-header-action-color',
              ratio === 0 ? 'white' : `rgba(0, 0, 0, ${ratio})`,
            )
            setRootCssVar('page-header-title-color', `rgba(0, 0, 0, ${ratio})`)
          }),
        )
        this.ready = true
      })
    },

    methods: {
      // 更新标题
      updateTitle() {
        document.title =
          this.title ||
          // 使用空白字符代替，不然某些平台下空格不会生效
          '\u200D'
      },

      // 更新微信分享数据
      updateWechatShareData() {
        if (this.share && this.share.title) {
          wechat.updateShareData({
            title: this.share.title,
            desc: this.share.desc,
            imgUrl: this.share.cover,
            link: this.share.url || location.href,
          })
        }
      },

      // 点击返回
      handleBackClick() {
        navigateBack()
      },

      // 点击分享
      handleShareClick() {
        dcb.share({
          ...this.share,
          url: this.share.url || location.href,
        })
      },
    },
  })
</script>

<style lang="scss" module>
  :root {
    --page-bg-color: #f5f5f5;
  }

  .page {
    width: 100%;
    min-height: 100vh;
    background-color: var(--page-bg-color);
    visibility: hidden;
    float: left;
    font-size: 0;

    &.ready {
      visibility: visible;
    }

    &.bgWhite {
      background-color: white;
    }
  }

  .hd {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
    height: 40px;
    padding: 0 12px;
    padding-top: var(--status-bar-height);
    box-sizing: content-box;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.custom {
      background: transparent;
      height: auto;
      padding: 0;

      > *:first-child {
        height: 40px;
        padding: 0 12px;
        padding-top: var(--status-bar-height);
        box-sizing: content-box;
      }
    }

    .back {
      flex-shrink: 0;
      font-size: 16px;
      margin-left: 0px;
      color: #222222;

      &:active {
        opacity: 0.8;
      }
    }

    .title {
      flex-grow: 1;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      font-size: 18px;
      line-height: 1.1;
      font-weight: bold;
      color: #222222;
      text-align: center;
      white-space: nowrap;
      max-width: 70%;
    }

    .extra {
      flex-shrink: 0;
      display: flex;

      .search {
        margin-right: 16px;
        font-size: 20px;

        &:active {
          opacity: 0.9;
        }
      }

      .share {
        font-size: 22px;

        &:active {
          opacity: 0.9;
        }
      }
    }
  }

  .bd {
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);

    .loading {
      width: 100vw;
      height: 100vh;
      background-color: var(--page-bg-color);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .error {
      width: 100vw;
      height: calc(100vh - var(--page-header-height));
      background-color: white;
      display: flex;
      align-items: center;
      justify-content: center;

      > * {
        margin-top: -100px;
        flex: 1;

        img {
          margin-bottom: 14px;
        }
      }
    }
  }

  .hdFade:not(.error) {
    .hd {
      background-color: rgba(255, 255, 255, var(--page-header-ratio));

      .back {
        color: var(--page-header-action-color);
      }

      .title {
        color: var(--page-header-title-color);
      }

      .extra {
        .share {
          color: var(--page-header-action-color);
        }
      }
    }

    .bd {
      margin-top: calc(0em - var(--page-header-height));
    }
  }
</style>
